/**
 * UI 服务器管理工具
 * 管理 Web UI 和 API 服务器的启动和停止
 */

import { spawn, ChildProcess } from 'child_process';
import { createServer } from 'net';
import path from 'path';
import { fileURLToPath } from 'url';
import chalk from 'chalk';
import ora from 'ora';
import fs from 'fs-extra';
import { getApiServer } from '../api/server.js';
import { logger } from './logger.js';

const __dirname = path.dirname(fileURLToPath(import.meta.url));

export interface ServerProcess {
  name: string;
  process?: ChildProcess;
  port: number;
  type: 'api' | 'ui';
}

/**
 * 检查端口是否可用
 */
export async function isPortAvailable(port: number): Promise<boolean> {
  return new Promise((resolve) => {
    const server = createServer();
    
    server.once('error', (err: any) => {
      if (err.code === 'EADDRINUSE') {
        resolve(false);
      } else {
        resolve(true);
      }
    });
    
    server.once('listening', () => {
      server.close();
      resolve(true);
    });
    
    server.listen(port);
  });
}

/**
 * 检查 API 服务器是否运行
 */
export async function isApiServerRunning(port: number): Promise<boolean> {
  try {
    const response = await fetch(`http://localhost:${port}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * 启动内置的 API 服务器
 */
export async function startApiServer(port: number): Promise<ServerProcess> {
  const spinner = ora('Starting API server...').start();
  
  try {
    const apiServer = getApiServer({ port });
    
    if (apiServer.isServerRunning()) {
      spinner.info('API server is already running');
      return {
        name: 'Workflow API Server',
        port,
        type: 'api',
      };
    }
    
    await apiServer.start();
    spinner.succeed(`API server started on port ${port}`);
    
    return {
      name: 'Workflow API Server',
      port,
      type: 'api',
    };
  } catch (error) {
    spinner.fail('Failed to start API server');
    throw error;
  }
}

/**
 * 查找 Web UI 目录
 */
async function findWebUIDirectory(): Promise<string | null> {
  // 可能的 Web UI 位置
  const possiblePaths = [
    // 在 monorepo 中
    path.resolve(__dirname, '../../../../apps/web'),
    path.resolve(__dirname, '../../../../apps/ui'),
    // 作为独立包
    path.resolve(__dirname, '../../../workflow-ui'),
    path.resolve(__dirname, '../../../web'),
    // 在当前项目中
    path.resolve(process.cwd(), 'apps/web'),
    path.resolve(process.cwd(), 'apps/ui'),
    path.resolve(process.cwd(), 'web'),
  ];
  
  for (const webPath of possiblePaths) {
    if (await fs.pathExists(webPath)) {
      // 检查是否有 package.json
      const packageJsonPath = path.join(webPath, 'package.json');
      if (await fs.pathExists(packageJsonPath)) {
        return webPath;
      }
    }
  }
  
  return null;
}

/**
 * 启动 Web UI 开发服务器
 */
export async function startWebUIServer(port: number): Promise<ServerProcess> {
  const spinner = ora('Starting Web UI server...').start();
  
  try {
    // 查找 Web UI 目录
    const webDir = await findWebUIDirectory();
    
    if (!webDir) {
      spinner.fail('Web UI directory not found');
      throw new Error(
        'Could not find Web UI directory. Please ensure the web app is available in your project.'
      );
    }
    
    spinner.text = `Found Web UI at ${path.relative(process.cwd(), webDir)}`;
    
    // 检查是否需要安装依赖
    const nodeModulesPath = path.join(webDir, 'node_modules');
    if (!await fs.pathExists(nodeModulesPath)) {
      spinner.text = 'Installing Web UI dependencies...';
      await runCommand('pnpm', ['install'], webDir);
    }
    
    // 检查 package.json 中的脚本
    const packageJson = await fs.readJson(path.join(webDir, 'package.json'));
    const hasDevScript = packageJson.scripts?.dev;
    const hasStartScript = packageJson.scripts?.start;
    
    let command: string;
    let args: string[];
    
    if (hasDevScript) {
      // 使用 dev 脚本
      command = 'pnpm';
      args = ['dev'];
      
      // 如果是 Vite，添加端口参数
      if (packageJson.devDependencies?.vite || packageJson.dependencies?.vite) {
        args.push('--', '--port', port.toString());
      }
    } else if (hasStartScript) {
      // 使用 start 脚本
      command = 'pnpm';
      args = ['start'];
    } else {
      spinner.fail('No dev or start script found in package.json');
      throw new Error('Web UI package.json must have a "dev" or "start" script');
    }
    
    // 启动 Web UI
    const webProcess = spawn(command, args, {
      cwd: webDir,
      detached: false,
      stdio: ['ignore', 'pipe', 'pipe'],
      env: {
        ...process.env,
        PORT: port.toString(),
        VITE_API_BASE_URL: `http://localhost:${getApiServer().getPort()}`,
      },
    });
    
    // 等待服务器就绪
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        webProcess.kill();
        spinner.fail('Web UI server failed to start');
        reject(new Error('Web UI server startup timeout'));
      }, 60000); // 60秒超时
      
      let isReady = false;
      
      webProcess.stdout?.on('data', (data) => {
        const output = data.toString();
        
        // 调试模式下输出日志
        if (process.env.DEBUG) {
          console.log(chalk.gray(`[Web UI] ${output.trim()}`));
        }
        
        // 检查服务器是否就绪
        if (
          output.includes('ready in') ||
          output.includes(`http://localhost:${port}`) ||
          output.includes('webpack compiled') ||
          output.includes('Compiled successfully')
        ) {
          if (!isReady) {
            isReady = true;
            clearTimeout(timeout);
            spinner.succeed(`Web UI server started on port ${port}`);
            resolve();
          }
        }
      });
      
      webProcess.stderr?.on('data', (data) => {
        const error = data.toString();
        
        // 忽略一些常见的警告
        if (
          error.includes('warning') ||
          error.includes('Warning') ||
          error.includes('DeprecationWarning')
        ) {
          if (process.env.DEBUG) {
            console.log(chalk.yellow(`[Web UI Warning] ${error.trim()}`));
          }
          return;
        }
        
        console.error(chalk.red(`[Web UI Error] ${error.trim()}`));
        
        if (error.includes('EADDRINUSE')) {
          clearTimeout(timeout);
          webProcess.kill();
          spinner.fail(`Port ${port} is already in use`);
          reject(new Error(`Port ${port} is already in use`));
        }
      });
    });
    
    return {
      name: 'Web UI Server',
      process: webProcess,
      port,
      type: 'ui',
    };
  } catch (error) {
    spinner.fail('Failed to start Web UI server');
    throw error;
  }
}

/**
 * 运行命令并等待完成
 */
async function runCommand(command: string, args: string[], cwd: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      cwd,
      stdio: 'inherit',
    });
    
    child.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`${command} ${args.join(' ')} failed with code ${code}`));
      }
    });
    
    child.on('error', reject);
  });
}

/**
 * 在默认浏览器中打开 URL
 */
export async function openBrowser(url: string): Promise<void> {
  const platform = process.platform;
  let command: string;
  let args: string[];
  
  if (platform === 'darwin') {
    command = 'open';
    args = [url];
  } else if (platform === 'win32') {
    command = 'cmd';
    args = ['/c', 'start', url];
  } else {
    command = 'xdg-open';
    args = [url];
  }
  
  try {
    spawn(command, args, { detached: true, stdio: 'ignore' }).unref();
  } catch (error) {
    logger.warn(`Could not open browser automatically. Please visit ${url} manually.`);
  }
}

/**
 * 设置优雅关闭
 */
export function setupGracefulShutdown(servers: ServerProcess[]): void {
  let isShuttingDown = false;
  
  const shutdown = async () => {
    if (isShuttingDown) return;
    isShuttingDown = true;
    
    console.log('\n' + chalk.yellow('Shutting down servers...'));
    
    for (const server of servers) {
      try {
        console.log(chalk.gray(`Stopping ${server.name}...`));
        
        if (server.type === 'api') {
          // 停止 API 服务器
          const apiServer = getApiServer();
          if (apiServer.isServerRunning()) {
            await apiServer.stop();
          }
        } else if (server.process) {
          // 停止子进程
          if (process.platform === 'win32') {
            spawn('taskkill', ['/pid', server.process.pid!.toString(), '/f', '/t']);
          } else {
            server.process.kill('SIGTERM');
          }
          
          // 等待进程结束
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // 强制结束
          if (!server.process.killed) {
            server.process.kill('SIGKILL');
          }
        }
        
        console.log(chalk.green(`✓ ${server.name} stopped`));
      } catch (error) {
        console.error(chalk.red(`Failed to stop ${server.name}:`, error));
      }
    }
    
    console.log(chalk.green('All servers stopped. Goodbye!'));
    process.exit(0);
  };
  
  // 处理各种关闭信号
  process.on('SIGINT', shutdown);
  process.on('SIGTERM', shutdown);
  process.on('exit', shutdown);
  
  // Windows 特定处理
  if (process.platform === 'win32') {
    import('readline').then((readline) => {
      const rl = readline.createInterface({
        input: process.stdin,
        output: process.stdout,
      });
      
      rl.on('SIGINT', shutdown);
    });
  }
}